import { Field, useField, useFormikContext } from "formik";
import React, { Fragment, useState } from "react";
import BaseElement from "components/FormElements/BaseElement";
import { packClasses } from "Utils";
import FlatCubeIcon from "components/Icons/FlatCubeIcon";

export default function AutocompleteTextInput(props) {
    const defaultProps = {
        label: "Text",
        id: "text-input",
        name: "text-input",
        type: "text",
        autoComplete: "off",
        value: "",
        isError: false,
        errorText: "",
        variant: false,
        onChange: () => {},
    };
    const useProps = Object.assign(defaultProps, {}, props);
    const [field, meta] = useField(props);
    const [searching, setSearching] = useState(false);
    const [hasResults, setHasResults] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [listItems, setListItems] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { setFieldValue } = useFormikContext();
    const { onRequestSearch, onEmptyFocus, setValue, ...remainder } = props;

    const doSearch = (term) => {
        if (searching) {
            return;
        }
        setSearching(true);
        if (onRequestSearch) {
            onRequestSearch(term)
                .then((data) => {
                    setSearching(false);
                    setSelectedIndex(0);
                    setListItems(data);
                    setMenuOpen(true);
                    setHasResults(true);
                })
                .catch((e) => {
                    setSearching(false);
                    setSelectedIndex(0);
                    setListItems([]);
                    setMenuOpen(false);
                    setHasResults(false);
                    console.info("Search failed", e);
                });
        }
    };

    let timeout;
    const handleSearch = (term) => {
        setFieldValue(field.name, term);
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            if (!term || term.length < 2) {
                return;
            }
            setMenuOpen(false);

            console.log("Search for ", term);
            doSearch(term);
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    };

    const menuClickHandler = (item, e) => {
        if (setValue) {
            setValue(item, field, setFieldValue);
        } else {
            setFieldValue(field.name, item.value);
        }
        setMenuOpen(false);
    };

    const keyDownHandler = (e) => {
        if (props.disabled) {
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (selectedIndex < listItems.length - 1) {
                setSelectedIndex(selectedIndex + 1);
            }
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1);
            }
        }
        if (e.key === "Enter") {
            e.preventDefault();

            if (props.setValue) {
                props.setValue(listItems[selectedIndex], field, setFieldValue);
            } else {
                setFieldValue(field.name, listItems[selectedIndex].value);
            }

            setMenuOpen(false);
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            setMenuOpen(false);
        }
    };

    const label = (
        <label
            htmlFor={useProps.id}
            className="block text-sm font-medium text-gray-700"
        >
            {props.label}
        </label>
    );

    const input = (
        <Fragment>
            <div className="relative">
                <Field
                    {...field}
                    {...remainder}
                    as="input"
                    type="text"
                    value={field.value || ""}
                    onChange={(e) => handleSearch(e.target.value)}
                    onFocus={(e) => {
                        if (hasResults) {
                            setMenuOpen(true);
                        } else if (onEmptyFocus && e.target.value.length < 1) {
                            onEmptyFocus()
                                .then((data) => {
                                    setSearching(false);
                                    setSelectedIndex(0);
                                    setListItems(data);
                                    setMenuOpen(true);
                                    setHasResults(true);
                                })
                                .catch((e) => {
                                    setSearching(false);
                                    setSelectedIndex(0);
                                    setListItems([]);
                                    setMenuOpen(false);
                                    setHasResults(false);
                                    console.info("Search failed", e);
                                });
                        }
                    }}
                    onBlur={(e) => {
                        // slight timeout to allow for the click handler on the menu
                        setTimeout(() => setMenuOpen(false), 200);
                    }}
                    onKeyDown={keyDownHandler}
                    autoComplete="off"
                    className={
                        (useProps.isError || (meta.touched && meta.error)
                            ? "focus:outline-none border-red-300 text-red-900 placeholder-red-300 focus:border-red-500 focus:ring-red-500"
                            : " border-gray-300 focus:border-green-500 focus:ring-green-500") +
                        " block w-full rounded-md py-2 px-4 shadow-sm disabled:bg-white sm:text-sm"
                    }
                />
                <div
                    className={"pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3".concat(
                        searching ? "" : " hidden"
                    )}
                >
                    <FlatCubeIcon
                        className="h-5 w-5 animate-spin text-gray-900"
                        aria-hidden="true"
                    />
                </div>
            </div>
            <div className={packClasses(menuOpen ? "" : "hidden", "relative")}>
                <div className="max-w-full">
                    <ul className="absolute top-0 left-0 z-50 min-w-full overflow-scroll bg-white shadow">
                        {listItems.map((item, idx) => (
                            <li
                                className={packClasses(
                                    selectedIndex === idx ? "bg-gray-100" : "",
                                    "cursor-pointer px-6 py-2 hover:bg-gray-100"
                                )}
                                onClick={menuClickHandler.bind(this, item)}
                                key={"autocomplete".concat(idx)}
                            >
                                <span className="text-sm font-light text-gray-700">
                                    {item.label}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>{" "}
        </Fragment>
    );

    const helperText = useProps.helpertext ? (
        <p className="mt-1.5 text-xs text-gray-500">{useProps.helpertext}</p>
    ) : (
        ""
    );

    const errorText =
        useProps.isError || (meta.touched && meta.error) ? (
            <p className="mt-2 text-sm text-red-600">
                {useProps.errorText}
                {meta.error}
            </p>
        ) : (
            ""
        );

    return (
        <BaseElement
            {...useProps}
            label={label}
            input={input}
            helpertext={helperText}
            errortext={errorText}
        />
    );
}
